import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { ArrowRightLeft, CheckCircle2, Trash2 } from "lucide-react";

import { lifecycleService, type StudentTransfer, type TransferType } from "../../services/lifecycleService";
import type { Class, Student } from "../../services/studentService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ReasonPanel } from "../inventory/InventoryParts";
import { SearchPick } from "../library/Pickers";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { CertificateButton, LearnerHistory } from "./LifecycleParts";
import { DIRECTIONS, day, learnerStatus, transferActions, transferConfig, transferStatus } from "./lifecycleLogic";

const route = (row: StudentTransfer) =>
  row.direction === "out" ? `${row.from_class_name ?? "—"} → ${row.to_school || "another school"}` : row.direction === "in" ? `${row.from_school || "another school"} → ${row.to_class_name ?? "—"}` : `${row.from_class_name ?? "—"} → ${row.to_class_name ?? "—"}`;

/** Transfers between classes and schools: request, approve, then complete on the day. */
export function TransfersPanel({ rows, types, learners, classes, canManage, isLoading, isError, onRetry, onChanged, initialView, creating, onCreate, onCloseCreate }: {
  rows?: StudentTransfer[];
  types: TransferType[];
  learners: Student[];
  classes: Class[];
  canManage: boolean;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
  initialView?: string;
  creating: boolean;
  onCreate: () => void;
  onCloseCreate: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const done = (text: string) => { setViewing(null); setNotice({ tone: "success", text }); onChanged(); };
  const act = useMutation({
    mutationFn: ({ row, action, reason }: { row: StudentTransfer; action: "approve" | "reject" | "complete" | "cancel"; reason?: string }) =>
      action === "approve" ? lifecycleService.approveTransfer(row.id)
        : action === "reject" ? lifecycleService.rejectTransfer(row.id, reason ?? "")
          : action === "cancel" ? lifecycleService.cancelTransfer(row.id, reason ?? "")
            : lifecycleService.completeTransfer(row.id),
    onSuccess: (_, { row, action }) => done({
      approve: `${row.student_name}'s transfer is approved. Complete it on the effective date.`,
      reject: `${row.student_name}'s transfer was rejected.`,
      cancel: `${row.student_name}'s transfer was cancelled.`,
      complete: row.direction === "out" ? `${row.student_name} has left for ${row.to_school}; the transfer certificate is ready.` : `${row.student_name} is now in ${row.to_class_name}.`,
    }[action]),
  });
  const remove = useMutation({
    mutationFn: (row: StudentTransfer) => lifecycleService.deleteStudentTransfer(row.id),
    onSuccess: (_, row) => done(`The transfer request for ${row.student_name} was deleted.`),
    onError: (error) => setNotice({ tone: "error", text: apiErrorMessage(error, "The request could not be deleted.") }),
  });

  const columns: RegisterColumn<StudentTransfer>[] = [
    { header: "Learner", cell: (row) => <span><span className="font-medium text-gray-900">{row.student_name}</span> <span className="text-xs text-gray-500">{row.student_id}</span></span>, exportValue: (row) => row.student_name },
    { header: "Student ID", cell: (row) => row.student_id, exportValue: (row) => row.student_id, exportOnly: true },
    { header: "Direction", cell: (row) => DIRECTIONS[row.direction], exportValue: (row) => DIRECTIONS[row.direction] },
    { header: "From → to", cell: (row) => route(row), exportValue: (row) => route(row) },
    { header: "Type", cell: (row) => row.transfer_type_name, exportValue: (row) => row.transfer_type_name },
    { header: "Effective", cell: (row) => day(row.effective_date), exportValue: (row) => day(row.effective_date) },
    { header: "Status", cell: (row) => <StatusDot {...transferStatus(row.status)} />, exportValue: (row) => transferStatus(row.status).label },
    { header: "Reason", cell: (row) => row.reason, exportValue: (row) => row.reason, exportOnly: true },
    { header: "Requested", cell: (row) => day(row.request_date), exportValue: (row) => day(row.request_date), exportOnly: true },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  const actions = row ? transferActions(row.status) : undefined;
  const early = row && row.effective_date > todayIso();

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        title="Transfers"
        description="Learners moving between classes, leaving for another school, or joining from one. A request is approved, then completed on the day it takes effect, which moves the learner and issues the transfer certificate."
        actions={canManage && <button type="button" className="btn btn-primary h-9 gap-1.5 text-sm" onClick={onCreate}><ArrowRightLeft className="h-4 w-4" /> New transfer</button>}
        rows={rows}
        config={transferConfig}
        initialState={initialView ? { view: initialView } : undefined}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        openLabel={(item) => (canManage && (item.status === "requested" || item.status === "approved") ? "Act" : "Open")}
        noun={["transfer", "transfers"]}
        exportName={{ file: "transfers", title: "Learner Transfers" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No transfers have been requested."
      />

      {row && actions && (
        <DetailsModal
          title={row.student_name}
          headline={{ label: DIRECTIONS[row.direction], value: route(row), note: `Takes effect ${day(row.effective_date)}` }}
          status={transferStatus(row.status)}
          facts={[
            ["Student ID", row.student_id],
            ["Type", row.transfer_type_name],
            ["Requested", day(row.request_date)],
            ["Decided by", row.approved_by_name || "—"],
            ["Certificate", row.transfer_certificate_issued ? `Issued ${day(row.transfer_certificate_date)}` : "—"],
            ["Last change", day(row.updated_at)],
          ]}
          sections={[{ title: "Reason", body: row.reason }, { title: "Notes", body: row.notes, tone: row.status === "rejected" || row.status === "cancelled" ? "danger" : "plain" }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              {canManage && actions.remove && (
                <button type="button" disabled={remove.isPending} onClick={() => window.confirm(`Delete the transfer request for ${row.student_name}?`) && remove.mutate(row)} className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              )}
              {row.status === "completed" && row.transfer_certificate_issued && <CertificateButton label="Transfer certificate" download={() => lifecycleService.downloadTransferCertificate(row.id)} />}
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && actions.approve && (
                <button type="button" className="btn btn-primary gap-1.5" disabled={act.isPending} onClick={() => act.mutate({ row, action: "approve" })}>
                  <CheckCircle2 className="h-4 w-4" /> Approve
                </button>
              )}
              {canManage && actions.complete && (
                <button type="button" className="btn btn-primary gap-1.5" disabled={act.isPending} onClick={() => (!early || window.confirm(`It takes effect on ${day(row.effective_date)}. Complete it now anyway?`)) && act.mutate({ row, action: "complete" })}>
                  <ArrowRightLeft className="h-4 w-4" /> {row.direction === "out" ? "Complete: the learner leaves" : `Complete: move into ${row.to_class_name}`}
                </button>
              )}
            </>
          }
        >
          {act.isError && <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{apiErrorMessage(act.error, "That could not be done.")}</p>}
          {canManage && actions.reject && (
            <ReasonPanel title="Reject the request" description="The learner stays where they are. The reason is kept on their record." placeholder="Why the transfer is not going ahead" context={{ key: "students.lifecycle_note", params: { kind: "transfer_reject", id: row.id } }} button="Reject request" danger busy={act.isPending} error={null} errorText="" onSubmit={(reason) => act.mutate({ row, action: "reject", reason })} />
          )}
          {canManage && row.status === "approved" && (
            <ReasonPanel title="Cancel the transfer" description="For a transfer that can no longer go ahead, e.g. the family changed plans." placeholder="Why it can no longer go ahead" context={{ key: "students.lifecycle_note", params: { kind: "transfer_cancel", id: row.id } }} button="Cancel transfer" danger busy={act.isPending} error={null} errorText="" onSubmit={(reason) => act.mutate({ row, action: "cancel", reason })} />
          )}
          <LearnerHistory studentId={row.student} />
        </DetailsModal>
      )}

      {creating && <TransferForm types={types.filter((item) => item.is_active)} learners={learners} classes={classes} onClose={onCloseCreate} onDone={(text) => { onCloseCreate(); done(text); }} />}
    </div>
  );
}

function TransferForm({ types, learners, classes, onClose, onDone }: { types: TransferType[]; learners: Student[]; classes: Class[]; onClose: () => void; onDone: (text: string) => void }) {
  const [student, setStudent] = useState("");
  const [direction, setDirection] = useState<StudentTransfer["direction"]>("internal");
  const [type, setType] = useState(types.length === 1 ? String(types[0].id) : "");
  const [toClass, setToClass] = useState("");
  const [school, setSchool] = useState("");
  const [requested, setRequested] = useState(todayIso());
  const [effective, setEffective] = useState(todayIso());
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const learner = learners.find((item) => String(item.id) === student);
  const destination = classes.find((item) => String(item.id) === toClass);
  const choices = classes.filter((item) => item.id !== learner?.current_class);
  const create = useMutation({
    mutationFn: () => lifecycleService.createStudentTransfer({
      student: Number(student),
      transfer_type: Number(type),
      direction,
      from_class: direction === "in" ? undefined : learner?.current_class ?? undefined,
      to_class: direction === "out" ? undefined : Number(toClass) || undefined,
      from_school: direction === "in" ? school.trim() : "",
      to_school: direction === "out" ? school.trim() : "",
      reason: reason.trim(),
      request_date: requested,
      effective_date: effective,
      notes: notes.trim(),
    }),
    onSuccess: (saved) => onDone(saved.status === "approved" ? `Transfer recorded for ${saved.student_name}; its type needs no approval, so it is ready to complete.` : `Transfer requested for ${saved.student_name}.`),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    create.mutate();
  };
  return (
    <FormModal
      id="transfer-form"
      title="New transfer"
      busy={create.isPending}
      error={create.isError ? apiErrorMessage(create.error, "The transfer could not be requested.") : undefined}
      saveLabel="Request transfer"
      note="Nothing moves until the transfer is completed."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Learner" divided={false} columns={1}>
        <Field as="div" label="Learner" hint={learner ? `${learner.current_class_name || "No class"} · ${learnerStatus(learner.status).label}` : "Learners in school or suspended."}>
          <SearchPick items={learners} value={student} onChange={setStudent} label={(item) => item.full_name} detail={(item) => `${item.student_id} · ${item.current_class_name || "no class"}`} placeholder="Search by name or number" empty="No learner matches" required />
        </Field>
      </FormSection>

      <FormSection title="The move" columns={2}>
        <Field label="Direction">
          <select value={direction} onChange={(event) => setDirection(event.target.value as StudentTransfer["direction"])} className="input mt-1">
            {(Object.keys(DIRECTIONS) as StudentTransfer["direction"][]).map((key) => <option key={key} value={key}>{DIRECTIONS[key]}</option>)}
          </select>
        </Field>
        <Field label="Type" hint={types.length ? undefined : "Add a transfer type in Settings first."}>
          <select required value={type} onChange={(event) => setType(event.target.value)} className="input mt-1">
            <option value="" disabled>Choose a type</option>
            {types.map((item) => <option key={item.id} value={item.id}>{item.name}{item.requires_approval ? "" : " (no approval needed)"}</option>)}
          </select>
        </Field>
        {direction !== "out" && (
          <Field label="Into class" hint={destination ? `${destination.available_spots} ${destination.available_spots === 1 ? "place" : "places"} free` : undefined}>
            <select required value={toClass} onChange={(event) => setToClass(event.target.value)} className="input mt-1">
              <option value="" disabled>Choose a class</option>
              {choices.map((item) => <option key={item.id} value={item.id}>{item.name} ({item.academic_year_name}) · {item.available_spots} free</option>)}
            </select>
          </Field>
        )}
        {direction !== "internal" && (
          <Field label={direction === "out" ? "Moving to school" : "Coming from school"} optional={direction === "in"}>
            <input required={direction === "out"} maxLength={200} value={school} onChange={(event) => setSchool(event.target.value)} className="input mt-1" placeholder="School name" />
          </Field>
        )}
        <Field label="Requested on">
          <input required type="date" value={requested} onChange={(event) => setRequested(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="Takes effect on">
          <input required type="date" min={requested} value={effective} onChange={(event) => setEffective(event.target.value)} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="Why" description="Kept on the learner's record and printed on nothing else." columns={1}>
        <Field as="div" label="Reason">
          <AIAssistField
            rows={2}
            value={reason}
            onChange={(next) => setReason(next)}
            placeholder="e.g. The family is moving to Gulu at the end of term."
            context={{ key: "students.lifecycle_note", params: { kind: "transfer_reason", student: Number(student) || null, direction, to_school: direction === "out" ? school : "", from_school: direction === "in" ? school : "", to_class_name: destination?.name ?? "" } }}
            draftLabel="Draft reason"
            draftHint="Jot the facts first and it writes them up. It never guesses a reason."
          />
        </Field>
        <Field label="Notes" optional>
          <textarea rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} className="input mt-1" placeholder="Anything the person completing it should know" />
        </Field>
      </FormSection>
    </FormModal>
  );
}
